import Ajv2020, { type Schema } from "ajv/dist/2020";
import { parse, parseAllDocuments } from "yaml";
import type { PlaceholderField } from "@/common/placeholders";

/**
 * Source of truth for the template gallery: where the manifest, template YAML
 * files, and the validation schema are fetched from, and the shape they're
 * expected to have.
 * See docs/architecture/ARCH-033-template-gallery.md and
 * docs/architecture/examples/README.md.
 */

const GALLERY_REPO_RAW_BASE =
	"https://raw.githubusercontent.com/stratonext/public-assets/main/";

export const GALLERY_MANIFEST_URL = `${GALLERY_REPO_RAW_BASE}templates/index.json`;

/**
 * Every imported template is validated against this JSON Schema, fetched at
 * import time — a schema change here takes effect immediately, no frontend
 * redeploy needed.
 */
export const GALLERY_SCHEMA_URL =
	"https://raw.githubusercontent.com/stratonext/public-assets/refs/heads/main/templates/stratonext-template-schema.yaml";

/** Origins the gallery is allowed to fetch a manifest, template, or schema from. */
const GALLERY_ALLOWED_ORIGINS = ["https://raw.githubusercontent.com"];

/** A template's declared placeholder for picking one of the workspace's Environments. */
export interface GalleryEnvironmentPlaceholder {
	label: string;
	description?: string;
	required?: boolean;
}

/** A template's declared placeholder for picking an Agent (or other assignee). */
export interface GalleryAgentPlaceholder {
	label: string;
	description?: string;
	required?: boolean;
}

/** A freeform value a template substitutes as a `{{key}}` token in its text fields —
 * the same placeholder shape workspace task templates declare. */
export type GalleryVariable = PlaceholderField;

/** The task-template fields a gallery template pre-fills, before substitution. */
export interface GalleryTemplateTask {
	name: string;
	description: string;
	instructions: string;
	verificationCriteria?: string;
}

/**
 * A single template, its frontmatter (metadata) and body (`task`/`placeholders`)
 * merged into one object. See `stratonext-template-schema.yaml` for the schema
 * this is validated against, and any template file for the on-disk format.
 */
export interface GalleryTemplate {
	id: string;
	name: string;
	description: string;
	cloudProvider: string;
	category: string;
	version: number;
	tags?: string[];
	task: GalleryTemplateTask;
	placeholders?: {
		environment?: GalleryEnvironmentPlaceholder;
		agent?: GalleryAgentPlaceholder;
		variables?: GalleryVariable[];
	};
}

/** A gallery template's listing in the manifest, before its YAML is fetched. */
export interface GalleryManifestEntry {
	id: string;
	name: string;
	description: string;
	cloudProvider: string;
	category: string;
	path: string;
}

/** True when `url`'s origin is allowed to be fetched for a manifest, template, or schema. */
export function isAllowedGalleryUrl(url: string): boolean {
	try {
		return GALLERY_ALLOWED_ORIGINS.includes(new URL(url).origin);
	} catch {
		return false;
	}
}

class GalleryFetchError extends Error {}

async function fetchTrustedText(url: string, what: string): Promise<string> {
	if (!isAllowedGalleryUrl(url)) {
		throw new GalleryFetchError(`${what} URL is not from a trusted source`);
	}
	const response = await fetch(url);
	if (!response.ok) {
		throw new GalleryFetchError(`${what} request failed (${response.status})`);
	}
	return response.text();
}

function requireString(value: unknown, field: string): string {
	if (typeof value !== "string" || value.length === 0) {
		throw new GalleryFetchError(
			`Manifest entry is missing required field "${field}"`,
		);
	}
	return value;
}

function toManifestEntry(value: unknown): GalleryManifestEntry {
	if (typeof value !== "object" || value === null) {
		throw new GalleryFetchError("Manifest entry is not an object");
	}
	const entry = value as Record<string, unknown>;
	return {
		id: requireString(entry.id, "id"),
		name: requireString(entry.name, "name"),
		description: requireString(entry.description, "description"),
		cloudProvider: requireString(entry.cloudProvider, "cloudProvider"),
		category: requireString(entry.category, "category"),
		path: requireString(entry.path, "path"),
	};
}

/** Fetches and validates the gallery manifest listing every available template. */
export async function fetchGalleryManifest(): Promise<GalleryManifestEntry[]> {
	const text = await fetchTrustedText(GALLERY_MANIFEST_URL, "Manifest");
	const body: unknown = JSON.parse(text);
	if (!Array.isArray(body)) {
		throw new GalleryFetchError("Manifest is not a list of templates");
	}
	return body.map(toManifestEntry);
}

/** Resolves a manifest entry's relative `path` to a fetchable, allowlisted URL. */
export function resolveGalleryTemplateUrl(path: string): string {
	return `${GALLERY_REPO_RAW_BASE}${path}`;
}

/**
 * Splits a template file's frontmatter (metadata) from its body (`task`/
 * `placeholders`) — two `---`-delimited YAML documents in one file — and
 * merges them into a single object.
 */
function parseTemplateDocument(text: string): unknown {
	const docs = parseAllDocuments(text);
	if (docs.length !== 2) {
		throw new GalleryFetchError(
			'Template must have a frontmatter block and a body, separated by "---"',
		);
	}
	const [frontmatter, body] = docs.map((d) => d.toJS());
	if (typeof frontmatter !== "object" || frontmatter === null) {
		throw new GalleryFetchError("Template frontmatter is not a YAML object");
	}
	if (typeof body !== "object" || body === null) {
		throw new GalleryFetchError("Template body is not a YAML object");
	}
	return { ...frontmatter, ...body };
}

/** Validates `data` against `schema`, throwing with every error joined into one message. */
function validateAgainstSchema(schema: unknown, data: unknown): void {
	const ajv = new Ajv2020({ allErrors: true, strict: false });
	const validate = ajv.compile(schema as Schema);
	if (!validate(data)) {
		throw new GalleryFetchError(
			`Template does not match the schema: ${ajv.errorsText(validate.errors)}`,
		);
	}
}

/**
 * Fetches, parses, and validates a single template from a trusted URL: splits
 * its frontmatter from its body, then validates the merged result against
 * `stratonext-template-schema.yaml`, fetched fresh alongside it.
 */
export async function fetchGalleryTemplate(
	url: string,
): Promise<GalleryTemplate> {
	// Checked up front, not just inside fetchTrustedText, so an untrusted `url`
	// never triggers even the (always-trusted) schema fetch.
	if (!isAllowedGalleryUrl(url)) {
		throw new GalleryFetchError("Template URL is not from a trusted source");
	}
	const [templateText, schemaText] = await Promise.all([
		fetchTrustedText(url, "Template"),
		fetchTrustedText(GALLERY_SCHEMA_URL, "Schema"),
	]);
	const template = parseTemplateDocument(templateText);
	const schema: unknown = parse(schemaText);
	validateAgainstSchema(schema, template);
	// Shape is now guaranteed by the schema validation above.
	return template as GalleryTemplate;
}
